import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { listWorldObjects, createWorldObject, deleteWorldObject } from '../services/worldObjects';
import { siteRow } from '../city/siteSurvey';
import { ROOMS } from '../rooms';
import { adminPanel, AdminClose } from './adminChrome';

// SitesPanel — ⚙ → Sites: where the town's next venue can stand.
//
// Khari, 2026-10-30: *"It is placed where the system sees fit."* The system can
// only see fit where it has looked, and only the browser can look (the road map
// is photographed from the city model here — see city/siteSurvey). So this is
// the looking: Survey measures the city and draws every candidate on it
// (SiteSurvey), you drop any that are wrong, and Save writes them as
// world_objects rows (kind 'site') through the same admin API the building
// grid and the venue tool use. The API's site chooser reads those rows; nothing
// else does.
//
// Unsaved until Save, like the building grid: a survey is cheap to run again and
// a bad one should be something you walk away from, not rows to clean up. Save
// REPLACES the saved set — the old rows go first, then the new ones — because a
// site list is an answer about the city as it is now, not a log.

const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = adminPanel({
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 236, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  maxHeight: 'calc(100vh - 260px)', overflowY: 'auto',
});
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const dot = (c) => ({ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: c, marginRight: 5 });
const deg = (r) => Math.round((((r || 0) * 180) / Math.PI % 360 + 360) % 360);

export default function SitesPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'sites';
  const survey = useWorldStore((s) => s.siteSurvey);
  const ask = useWorldStore((s) => s.askSiteSurvey);
  const asked = useWorldStore((s) => s.siteSurveyAsk);
  const drop = useWorldStore((s) => s.dropSurveySite);
  const saved = useWorldStore((s) => s.savedSites);
  const setSaved = useWorldStore((s) => s.setSavedSites);
  const setSurvey = useWorldStore((s) => s.setSiteSurvey);
  const registry = useWorldStore((s) => s.venueRegistry);
  const planView = useWorldStore((s) => s.planView);
  const setPlanView = useWorldStore((s) => s.setPlanView);
  // The survey runs where the city is drawn (SiteSurvey), which a virtual room is not.
  const interior = useWorldStore((s) => s.interior);
  const away = !!interior && ROOMS[interior]?.placement !== 'sited';
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [waiting, setWaiting] = useState(0);

  // The saved rows, whenever the tool opens — admin-only state, read where it is used.
  useEffect(() => {
    if (!ON) return undefined;
    let dead = false;
    listWorldObjects('street', 'site').then((r) => { if (!dead) setSaved(r || []); });
    return () => { dead = true; };
  }, [ON, setSaved]);
  // "Surveying…" until the answer for THIS press arrives.
  useEffect(() => { if (survey && waiting) setWaiting(0); }, [survey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Closing the tool abandons a survey in flight (SiteSurvey drops its answer).
  useEffect(() => { if (!ON) setWaiting(0); }, [ON]);

  if (!ON) return null;
  const sites = survey?.sites || [];
  const taken = Object.keys(registry || {}).length;

  const run = () => { setMsg(''); setSurvey(null); setWaiting(asked + 1); ask(); };

  const save = async () => {
    if (!sites.length) return;
    setBusy(true); setMsg('');
    try {
      // Re-read rather than trusting the list from when the tool opened: another
      // admin, or another tab, may have saved since.
      // An EMPTY LOT is not something a survey finds (it looks for buildings), so
      // a re-survey must never wipe one (2026-10-31: the lot behind the spawn,
      // migration 460). Only surveyed buildings are replaced.
      const all = await listWorldObjects('street', 'site');
      const lots = all.filter((r) => r.data?.kind === 'lot');
      const old = all.filter((r) => r.data?.kind !== 'lot');
      for (const r of old) await deleteWorldObject(r.object_id);
      const fresh = [];
      for (const s of sites) {
        const row = await createWorldObject(siteRow(s));
        if (row) fresh.push(row);
      }
      setSaved([...lots, ...fresh]);
      setSurvey(null);
      setMsg(`saved ${fresh.length} site${fresh.length === 1 ? '' : 's'} (replaced ${old.length})`);
    } catch (e) {
      setMsg(e.message || 'save failed');
      listWorldObjects('street', 'site').then((r) => setSaved(r || []));
    } finally { setBusy(false); setTimeout(() => setMsg(''), 5000); }
  };

  return (
    <div style={panel}>
      <AdminClose />
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>
        Sites
      </div>

      {!planView && (
        <button style={{ ...btn, width: '100%', marginBottom: 9, background: 'rgba(157,255,125,0.14)', color: '#c8ffb8', borderColor: 'rgba(157,255,125,0.4)' }}
          onClick={() => setPlanView(true)}>
          Turn on Build view
        </button>
      )}

      <button style={{ ...btn, width: '100%', marginBottom: 8, opacity: away ? 0.45 : 1 }} onClick={run} disabled={busy || !!waiting || away}>
        {away ? 'Step outside to survey' : waiting ? 'Surveying…' : survey ? 'Survey again' : 'Survey the city'}
      </button>

      <div style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
        <div><span style={dot('#ffd27d')} />{survey ? `${sites.length} found, unsaved` : 'no survey yet'}</div>
        <div><span style={dot('#7dd3ff')} />{saved.length} saved</div>
        <div><span style={dot('#ff6bd6')} />{taken} taken by a venue</div>
      </div>

      {/* WHY SO FEW. Every rule that turned a building down, counted — the
          answer to "why not that one" without reading the survey's code. */}
      {survey?.rejected && (
        <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 8, lineHeight: 1.4 }}>
          of {survey.buildings} buildings — not solid {survey.rejected.solid} · no street front {survey.rejected.frontage}
          {' '}· no clear door {survey.rejected.door} · near a venue {survey.rejected.venue} · plaza/park/court/subway {survey.rejected.zone}
          {' '}· too close to a better one {survey.rejected.spacing}
        </div>
      )}
      {survey?.note && <div style={{ fontSize: 11, color: '#ff9d7d', marginBottom: 8 }}>{survey.note}</div>}

      {sites.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto', marginBottom: 9 }}>
          {sites.map((s) => (
            <div key={s.ref} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 10.5, padding: '4px 6px', borderRadius: 7, background: 'rgba(255,178,94,0.1)' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.ref}>{s.ref}</span>
              <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{s.w.toFixed(0)}×{s.d.toFixed(0)} · {deg(s.rotY)}°</span>
              {/* Drop a wrong one before saving: the survey is a first answer. */}
              <button onClick={() => drop(s.ref)} title="Leave this one out"
                style={{ ...btn, padding: '0 6px', fontSize: 11, lineHeight: '18px' }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <button disabled={busy || !sites.length}
        style={{ ...btn, width: '100%', background: sites.length ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: sites.length ? '#1c1206' : '#ffd27d' }}
        onClick={save}>{busy ? '…' : `Save ${sites.length || ''} site${sites.length === 1 ? '' : 's'}`}</button>
      {msg && <div style={{ fontSize: 10.5, marginTop: 8, color: /saved/.test(msg) ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}

      <div style={{ opacity: 0.55, fontSize: 10.5, marginTop: 9, lineHeight: 1.4 }}>
        Existing, unused buildings with a street front, clear of the venues and
        the open places. The tick on each points out of its door. Save replaces
        every saved site; the pipeline picks from those.
      </div>
    </div>
  );
}
